import React, { memo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Dropdown, SlideUp, Text } from 'common-components'
import { useDropdownState } from 'hooks/useDropdownState'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import * as Styled from './styled'
import { IModelDropdownProps } from './types'
import { IAIImageModel } from 'store/types/aiImage'
import ImageModelItem from './ImageModelItem'
import { userSelectors } from 'store/features/user/selectors'
import { isPremiumModel, handlePremiumModelClick } from 'utils'
import CrownIcon from 'assets/svgr-icons/Crown.svg'

interface IHeaderContentProps {
  activeModel: {
    key: string
    label: string
  }
  isPremium?: boolean
}

const HeaderContent: React.FC<IHeaderContentProps> = ({
  activeModel,
  isPremium
}) => {
  const { label } = activeModel

  return (
    <Styled.HeaderContent>
      <Styled.LabelWrapper>
        <Text type="h2" color="secondarySemiDark">
          {label}
        </Text>
        {isPremium && (
          <Styled.CrownBadgeIcon
            icon={<CrownIcon />}
            size="small"
            color="goldDark"
          />
        )}
      </Styled.LabelWrapper>
    </Styled.HeaderContent>
  )
}

const ModelDropdownContent: React.FC<{
  models: IAIImageModel[]
  selectedModelCode: string
  onSelect: (modelCode: string) => void
  userCurrentPlanId: number
}> = ({ models, selectedModelCode, onSelect, userCurrentPlanId }) => {
  return (
    <Styled.DropdownListWrapper>
      {models.map(model => {
        const isSelected = model.code === selectedModelCode
        const isPremium = isPremiumModel(model, userCurrentPlanId)

        return (
          <ImageModelItem
            key={model.id}
            model={model}
            isSelected={isSelected}
            onSelect={() => onSelect(model.code)}
            isPremium={isPremium}
          />
        )
      })}
    </Styled.DropdownListWrapper>
  )
}

const ModelDropdown: React.FC<IModelDropdownProps> = ({
  models,
  selectedModelCode,
  onModelSelect
}) => {
  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    openCallback,
    closeCallback
  )

  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const userCurrentPlanId = useSelector(userSelectors.userCurrentPlanId)

  const handleSelect = useCallback(
    (modelCode: string) => {
      const model = models.find((m: IAIImageModel) => m.code === modelCode)

      if (handlePremiumModelClick(model, userCurrentPlanId)) {
        return
      }

      if (modelCode !== selectedModelCode) {
        onModelSelect(modelCode)
      }
    },
    [models, userCurrentPlanId, selectedModelCode, onModelSelect]
  )

  const activeModel =
    models.find(model => model.code === selectedModelCode) || models[0]
  const activeModelData = {
    key: activeModel.code,
    label: activeModel.name
  }
  const isSelectedModelPremium = isPremiumModel(activeModel, userCurrentPlanId)

  if (isTablet && isOpen) {
    return (
      <SlideUp isOpen={isOpen} onClose={closeDropdown}>
        <ModelDropdownContent
          models={models}
          selectedModelCode={selectedModelCode}
          onSelect={modelCode => {
            handleSelect(modelCode)
            closeDropdown()
          }}
          userCurrentPlanId={userCurrentPlanId}
        />
      </SlideUp>
    )
  }

  return (
    <Dropdown
      headerContent={
        <HeaderContent
          activeModel={activeModelData}
          isPremium={isSelectedModelPremium}
        />
      }
      mainContent={
        <ModelDropdownContent
          models={models}
          selectedModelCode={selectedModelCode}
          onSelect={modelCode => {
            handleSelect(modelCode)
            closeDropdown()
          }}
          userCurrentPlanId={userCurrentPlanId}
        />
      }
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
    />
  )
}

export default memo(ModelDropdown)
